/**
 * ServicesCatalog — CRUD do catálogo de serviços do tenant.
 *
 * Movido de TenantAgenda para ser reutilizado como aba de Financeiro
 * (iter_27, 2026-03). O catálogo é semanticamente parte da precificação
 * (junto com Pacotes/Planos/Assinaturas/Pagamentos).
 */
import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Pencil, Plus, Loader2, Trash2, TrendingUp, TrendingDown, CheckCircle2, Search } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import IconAction from "@/components/IconAction";
import { usePagedSearch } from "@/lib/usePagedSearch";
import PaginationBar from "@/components/PaginationBar";

const TID = {
  serviceName: "service-name",
  serviceDuration: "service-duration",
  servicePrice: "service-price",
  serviceSubmit: "service-submit",
};

export default function ServicesCatalog() {
  const [items, setItems] = useState([]);
  const [insights, setInsights] = useState({});
  const [loading, setLoading] = useState(true);
  const blankForm = { name: "", description: "", duration_minutes: 30, price: 0, color: "221 83% 53%", return_after_days: "" };
  const [form, setForm] = useState(blankForm);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [svc, ins] = await Promise.all([
        api.get("/tenant/services"),
        api.get("/tenant/services/duration-insights"),
      ]);
      setItems(svc.data.items || []);
      const map = {};
      for (const s of (ins.data.services || [])) map[s.service_id] = s;
      setInsights(map);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const adoptDuration = async (s) => {
    if (!window.confirm(`Ajustar duração de "${s.name}" para a média real?`)) return;
    try {
      const { data } = await api.post(`/tenant/services/${s.id}/adopt-duration`);
      toast.success(`Duração ajustada para ${data.new_duration_minutes} min`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const startEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.name || "",
      description: s.description || "",
      duration_minutes: s.duration_minutes || 30,
      price: s.price || 0,
      color: s.color || "221 83% 53%",
      return_after_days: s.return_after_days || "",
    });
    setTimeout(() => document.querySelector("[data-testid='service-name']")?.focus(), 50);
  };

  const cancelEdit = () => { setEditing(null); setForm(blankForm); };

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const payload = {
        ...form,
        duration_minutes: Number(form.duration_minutes),
        price: Number(form.price),
        return_after_days: form.return_after_days ? Number(form.return_after_days) : null,
      };
      if (editing) {
        await api.put(`/tenant/services/${editing.id}`, payload);
        toast.success("Serviço atualizado");
      } else {
        await api.post("/tenant/services", payload);
        toast.success("Serviço criado");
      }
      setForm(blankForm);
      setEditing(null);
      load();
    } catch (er) { toast.error(formatApiError(er)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir este serviço?")) return;
    try { await api.delete(`/tenant/services/${id}`); load(); }
    catch (er) { toast.error(formatApiError(er)); }
  };

  // Busca + paginação client-side (10/página)
  const { q, setQ, page, setPage, totalPages, total, pageItems } = usePagedSearch(items, { pageSize: 10 });

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <Card className="p-6 rounded-tenant border-border">
        <div className="flex items-center justify-between mb-4">
          <div className="font-display text-lg font-semibold">
            {editing ? "Editar serviço" : "Novo serviço"}
          </div>
          {editing && (
            <Button size="sm" variant="ghost" onClick={cancelEdit} data-testid="service-edit-cancel">Cancelar</Button>
          )}
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5">
            <Label>Nome</Label>
            <Input required data-testid={TID.serviceName} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Duração (min)</Label>
              <Input required type="number" min={5} data-testid={TID.serviceDuration} value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Preço (R$)</Label>
              <Input required type="number" step="0.01" min={0} data-testid={TID.servicePrice} value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Sugerir retorno após (dias)</Label>
            <Input
              type="number" min={1} max={365}
              placeholder="Ex.: 30 (corte), 15 (manicure), 45 (coloração)"
              data-testid="service-return-after"
              value={form.return_after_days}
              onChange={(e) => setForm({ ...form, return_after_days: e.target.value })}
            />
            <p className="text-xs text-muted-foreground">Usado para sugerir retornos e detectar clientes em risco.</p>
          </div>
          <Button type="submit" disabled={busy} data-testid={TID.serviceSubmit} className="w-full gap-2"
            style={{ background: `hsl(var(--tenant-primary))`, color: `hsl(var(--tenant-primary-foreground))` }}>
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            {editing ? <><Pencil className="w-4 h-4" /> Salvar alterações</> : <><Plus className="w-4 h-4" /> Criar serviço</>}
          </Button>
        </form>
      </Card>

      <Card className="rounded-tenant border-border overflow-hidden lg:col-span-2">
        <div className="px-5 py-4 border-b border-border flex items-center gap-3 flex-wrap">
          <div className="font-display font-semibold">Catálogo</div>
          <div className="flex-1" />
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <Input
              placeholder="Buscar serviço..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-8 h-8 w-56 text-sm"
              data-testid="services-search"
            />
          </div>
        </div>
        {loading ? <div className="p-10 text-center text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin inline" /></div> :
          items.length === 0 ? <div className="p-10 text-center text-muted-foreground">Nenhum serviço cadastrado.</div> :
          total === 0 ? <div className="p-10 text-center text-muted-foreground">Nenhum serviço encontrado para &quot;{q}&quot;.</div> :
          <>
          <ul className="divide-y divide-border">
            {pageItems.map((s) => {
              const ins = insights[s.id];
              const hasInsight = ins && ins.sample_size >= 3;
              return (
              <li key={s.id} className="px-5 py-3 space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-2 h-10 rounded-full shrink-0" style={{ background: `hsl(${s.color})` }} />
                    <div className="min-w-0">
                      <div className="font-medium truncate">{s.name}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {s.duration_minutes} min · R$ {(s.price || 0).toFixed(2)}
                        {s.return_after_days ? ` · retorno em ${s.return_after_days}d` : ""}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <IconAction icon={Pencil} label="Editar serviço" testid={`service-edit-${s.id}`} onClick={() => startEdit(s)} />
                    <IconAction icon={Trash2} label="Excluir serviço" testid={`service-remove-${s.id}`} className="text-destructive hover:text-destructive" onClick={() => remove(s.id)} />
                  </div>
                </div>
                {hasInsight && ins.suggestion !== "ok" && (
                  <div
                    data-testid={`service-insight-${s.id}`}
                    className={`ml-5 flex items-center gap-2 text-xs p-2 rounded-md border ${
                      ins.suggestion === "aumentar"
                        ? "bg-amber-500/10 border-amber-500/30 text-amber-800"
                        : "bg-emerald-500/10 border-emerald-500/30 text-emerald-800"
                    }`}
                  >
                    {ins.suggestion === "aumentar"
                      ? <TrendingUp className="w-3.5 h-3.5 shrink-0" />
                      : <TrendingDown className="w-3.5 h-3.5 shrink-0" />}
                    <span className="flex-1">
                      Real médio: <strong>{ins.actual_avg_minutes} min</strong>{" "}
                      ({ins.sample_size} atendimentos) —{" "}
                      {ins.suggestion === "aumentar" ? "está durando mais" : "está durando menos"}{" "}
                      que os {ins.configured_minutes} min cadastrados
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs gap-1 shrink-0"
                      onClick={() => adoptDuration(s)}
                      data-testid={`service-adopt-${s.id}`}
                    >
                      <CheckCircle2 className="w-3 h-3" /> Ajustar para {ins.actual_avg_minutes}min
                    </Button>
                  </div>
                )}
                {hasInsight && ins.suggestion === "ok" && (
                  <div className="ml-5 text-xs text-muted-foreground flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                    Duração cadastrada bate com a real ({ins.actual_avg_minutes} min, {ins.sample_size} atend.)
                  </div>
                )}
              </li>
              );
            })}
          </ul>
          <div className="px-5 py-3 border-t border-border">
            <PaginationBar
              page={page} totalPages={totalPages} total={total}
              onChange={setPage} label="serviço" testid="services-pagination"
            />
          </div>
          </>}
      </Card>
    </div>
  );
}
